import { Section } from '@/courses/models/section.model';
import Component from 'vue-class-component';
import { Mixins, Prop } from 'vue-property-decorator';
import KEYS from '@/values/keys';
import { HOMEWORK_STATUS, OBJECT_HOMEWORK_STATUS, _HOMEWORKS_STORE, GRADING_SCALES } from '@/homework/values/homeworks.values';
import { SECTION_TYPES, TYPE_BOARD_DRAFT_RESPONSE_OPTIONS } from '@/content/values/sections';
import EdHomeworkViewer from '@/homework/component/ed-homework-viewer/ed-homework-viewer.vue';
import Moment from 'moment';
import { CourseToolsMixin } from '@/courses/mixins/course-tools.mixin';
import { SCREEN_MIRRORING_EVENTS } from '@/values/screen.mirroring.values';
import { COMPONENT_TYPE } from '@/courses/values/courses.values';
import { PDF_DRAW_UTILS } from '@/values/ed-pdf-draw.values';

@Component({})
export default class EdSectionObjectHomeworkInfo extends Mixins(CourseToolsMixin) {

  @Prop() readonly section: Section;
  @Prop() readonly courseId: number;

  public OBJECT_HOMEWORK_STATUS = OBJECT_HOMEWORK_STATUS;

  get homeworkStatus() {
    let homeworkStatus = '';
    if(this.isStudentAndSectionHomework()) {
        homeworkStatus = OBJECT_HOMEWORK_STATUS.PENDING;
        if(this.section.homeworkStatus) {
          switch(this.section.homeworkStatus) {
            case HOMEWORK_STATUS.NEW:
            case HOMEWORK_STATUS.SEEN:
            case HOMEWORK_STATUS.FEEDBACK_DRAFT:
              homeworkStatus = OBJECT_HOMEWORK_STATUS.SENT;
            break;
            case HOMEWORK_STATUS.GRADED:
            case HOMEWORK_STATUS.FINISHED:
              homeworkStatus = OBJECT_HOMEWORK_STATUS.GRADED;
            break;
          }
        } 
      }
    return homeworkStatus;
  }

  get canAnswer() {    
    return this.getIsTaskObject(this.section.type) && (this.homeworkStatus ==  OBJECT_HOMEWORK_STATUS.PENDING);
  }

  get classId() {
    return this.section.classId;
  }

  get course() {
    return this.getCourseFromStore({courseId: this.courseId, classId: this.classId });
  }

  get canOpenWithCurrentVersion() : boolean {
    return (this.validateDraftBoardOptions(this.section)
            && this.section.homeworkInfo
            && this.section.homeworkInfo.type === SECTION_TYPES.LESSON_BOARD.code
            && this.section.homeworkStatus === HOMEWORK_STATUS.DRAFT)
            || this.section.homeworkStatus != HOMEWORK_STATUS.DRAFT
            || !this.validateDraftBoardOptions(this.section);
  } 

  get grade() {
    if(this.feedback) {
      return this.course.hasGrading ? this.section.gradeName || this.section.homeworkFeedbackInfo.grade : this.section.homeworkFeedbackInfo.grade;
    }
    return null;
  }

  get gradeDetails() {
    if(this.course.hasGrading){
      if(this.section.homeworkStatus == HOMEWORK_STATUS.GRADED || this.section.homeworkStatus == HOMEWORK_STATUS.FINISHED) {
        if (this.section?.gradingDetails?.length == 1) {
          return `${this.$t('courses.grade.gradeW')}: ${this.section.gradingDetails[0].gradeName ? this.section.gradingDetails[0].gradeName : this.section.gradingDetails[0].type == GRADING_SCALES.PERCENTAGE ? `${this.section.gradingDetails[0].value}%` : this.section.gradingDetails[0].points },   
            ${this.$t('courses.grade.points')}: ${this.section.gradingDetails[0].points}/${this.section.gradingDetails[0].itemPoints}, 
            ${this.section.gradingDetails[0].value}%`
        } else if (this.section?.gradingDetails?.length > 1) {
          let grades = [];
          this.section.gradingDetails.forEach((el, i) => {
            grades.push(`${this.$t('courses.grade.gradeW')} Q${i+1}: ${el.gradeName ? el.gradeName :  el.type == GRADING_SCALES.PERCENTAGE ? `${el.value}%` : el.points}, 
              ${this.$t('courses.grade.points')}: ${el.points}/${el.itemPoints}, 
              ${el.value}%`)
          })
          return grades
        } else {
          return this.section.homeworkGrade
        }
      }
    } else{
      return this.section.homeworkGrade;
    }
  }

  get feedback() {
    return this.isStudentAndSectionHomework() && (
        this.section.homeworkStatus == HOMEWORK_STATUS.GRADED
        || this.section.homeworkStatus == HOMEWORK_STATUS.FINISHED)
        && this.section.homeworkFeedbackInfo;
  }

  get isTeacher() {
    return this.$store.state.AuthStore.role === KEYS.ROLES.PUBLISHER;
  }

  get isPresentationTeacherMode() : boolean {
    return this.$store.state.CourseStore.isPresentationTeacherMode;
  }

  isStudentAndSectionHomework() {
    return this.$store.state.AuthStore.role == KEYS.ROLES.BUYER && this.section.homework;
  }

  get canAnswerDisabled() {
    let res = false;
    const now = Moment().format('YYYY-MM-DD');
    if(this.$store.getters['AuthStore/isStudent']) {
      if(this.section.submissionDates) {
        if(this.section.submissionDates.lastDate) {
          res = Moment(now).isAfter(this.section.submissionDates.lastDate);
        } else if(this.section.submissionDates.dueDate) {
          res = Moment(now).isAfter(this.section.submissionDates.dueDate);
        }
      }
    }
    return res;
  }

  get titleAnswerIcon() {
    return this.canAnswerDisabled ? this.$i18n.t('courses.objects.homeworkExpired') : this.$i18n.t('courses.objects.answerHomework');
  }

  get homeworkInfoClass() {
    return {
      'ed-section-object-homework-info--disabled': this.canAnswerDisabled
    }
  }

  get canSeeStudentResponse(): boolean {
    return !!this.section.homeworkInfo;
  }

  get studentResponseTitle(): string {
    if(this.canSeeStudentResponse) {
      return String(this.$i18n.t('homeworks.homeworkResponseView'))
    }
    return String(this.$i18n.t('homeworks.homeworkResponseSent'));
  }

  getIsTaskObject(type: string) {
    switch(this.section.type) {
      case SECTION_TYPES.LESSON_PDF.code:
      case SECTION_TYPES.LESSON_SPLIT_WRITE.code:
      case SECTION_TYPES.LESSON_SPLIT_TYPE.code:
      case SECTION_TYPES.LESSON_MATERIAL.code:
      case SECTION_TYPES.LESSON_TEXT.code:  
        return true;
      break;
      case SECTION_TYPES.LESSON_XLS.code:
      case SECTION_TYPES.LESSON_XLSX.code:
      case SECTION_TYPES.LESSON_PPT.code:
      case SECTION_TYPES.LESSON_PPTX.code:
      case SECTION_TYPES.LESSON_DOC.code:
      case SECTION_TYPES.LESSON_DOCX.code:
      case SECTION_TYPES.LESSON_EPUB.code:  
        return this.getIsFileTask();
      break;
    }
  }

  getIsFileTask() {
    let isPDF = false;
    if(this.section.assetsInfo) {
      if(Array.isArray(this.section.assetsInfo)) {
        isPDF = this.section.assetsInfo[0].pdfUrl ? true : false;
      }
    }
    return (this.section.type == SECTION_TYPES.LESSON_PDF.code) || isPDF;
  }

  goToAnswerHomework(emitMirroringEvent : boolean = true) {
    if(!this.canAnswerDisabled) {
      if(!this.canOpenWithCurrentVersion){
        this.showCantOpenHomeworkModal();
        return;
      }

      if(this.canAnswer) {
        let responseDraft = null;
        let viewer = SECTION_TYPES[this.section.type].viewer;
        if(this.getIsTaskObject(this.section.type)) {
          /* Set viewer */
          viewer = SECTION_TYPES[this.section.type].viewerHomework;
          /* Check draft */
          if(this.section.homeworkInfo) {
            responseDraft = this.section.homeworkInfo;
          }
        }
        this.$EdModal.createModal(EdHomeworkViewer, {
          section: {
            id: this.section.sectionId,
            name: this.section.name,
            assetsInfo: this.section.assetsInfo,
            type: this.section.type,
            text: this.section.text,
            sectionId: this.section.sectionId,
            homework: true,
            classId: this.classId,
            sections: this.section.sections,
            homeworkStatus: this.section.homeworkStatus,
            template: this.section.template,
            sectionMaterial: this.section.sectionMaterial,
            status: this.section.status,
            version: this.section.version
          },
          responseDraft,
          courseId: this.courseId,
          viewer,
          canResponse: true,
          isFeedback: false
        });
      }

      if( emitMirroringEvent ) {
        var target = this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_SECTION_OBJECT_HOMEWORK);
        this.$edEventBus.$emit( SCREEN_MIRRORING_EVENTS.CLICK, this, {target:target});
      }
    }
  }

  public validateDraftBoardOptions(section: Section) : boolean {
    return TYPE_BOARD_DRAFT_RESPONSE_OPTIONS.indexOf(section.type) >= 0;
  }

  public showCantOpenHomeworkModal() : void{
    this.$EdModal.createMessageModal({
      title: this.$i18n.t('common.information'),
      body: this.$i18n.t('homeworks.cantOpenHomework'),
      type: KEYS.EVENT_TYPES.WARNING
    });
  }

  public async openStudentResponse() {
    if (this.canSeeStudentResponse) {
      const response: boolean = await this.validateHomeworkStatus(this.section.homeworkInfo.homeworkAssetInfo.hash);
      if (!response) {
        this.createMessageModal(String(this.$i18n.t('homeworks.documentStatus')), KEYS.EVENT_TYPES.WARNING);
        return;
      }
      const homeworkAssetInfo = this.section.homeworkInfo.homeworkAssetInfo;
      homeworkAssetInfo.pdfHash = homeworkAssetInfo.pdfHash ?? homeworkAssetInfo.hash;
      homeworkAssetInfo.pdfUrl = homeworkAssetInfo.pdfUrl ?? homeworkAssetInfo.url;
      const info = {
        ...this.section.homeworkInfo,
        assetsInfo: [homeworkAssetInfo]
      }
      this.openViewer(info, false, false);
    
    }
  }

  public openFeedback() {
    let info = {
      ...this.section.homeworkFeedbackInfo,
      assetsInfo: [this.section.homeworkFeedbackInfo.homeworkAssetInfo]
    };
    this.openViewer(info, false, true);

  }

  public openViewer(info: any, canResponse: boolean, isFeedback: boolean = false) {
    this.$EdModal.createModal(EdHomeworkViewer, {
      section: {
        id: info.sectionId,
        name: info.name,
        assetsInfo: info.assetsInfo,
        type: info.type,
        text: info.text,
        sectionId: info.sectionId,
        homework: true,
        classId: this.section.classId,
        sections: info.sections,
        homeworkStatus: info.homeworkStatus,
        template: info.template,
        sectionMaterial: info.sectionMaterial,
        status: info.status,
        homeworkDraftAssetInfo: null,
        version: info.version
      },
      feedback: info,
      courseId: this.courseId,
      viewer: SECTION_TYPES[info.type].viewerHomework,
      canResponse,
      isFeedback
    });
  }

  openCompletedTaskByStudent({sectionId , courseId , classId = null}:
    { sectionId: number, courseId: number, classId?: number}) {
      this.$Courses.Store.getCompletedTaskByStudentA({
        sectionId , courseId , classId
      });

      this.goToComponentSelected({
          courseId,
          classId, 
          componentInfo: { 
            name: COMPONENT_TYPE.HOMEWORK_TEACHER_SECTION_PERSON_COURSE_VIEWER.code,
            section: null,
            edition: false
          }
        }
      );
  }

  public viewCompletedTaskBySection() {
    this.openCompletedTaskByStudent({
      sectionId: this.section.sectionId,
      courseId: this.courseId,
      classId: this.section.classId
    });
  }

  public goToAnswerHomeworkWithoutMirroring() {
    this.goToAnswerHomework(false);
  }

  public target(screenMirroringEventName:string) {
    return screenMirroringEventName.replace('{sectionId}', this.section.sectionId?.toString());
  }

  private validateHomeworkStatus(hash: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.$store.dispatch(_HOMEWORKS_STORE.ACTIONS.GET_HOMEWORK_CONVERTER_STATUS, hash)
      .then(response => resolve(response))
      .catch(err => reject(err));
    });
  }

  private createMessageModal(body: string , type: string) {
    this.$EdModal.createMessageModal({
      title: this.$i18n.t('common.information'),
      body: body,
      type: type
    });
  }

  public created() {
    this.$edEventBus.$on(this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_SECTION_OBJECT_HOMEWORK), this.goToAnswerHomeworkWithoutMirroring);
  }

  public beforeDestroy() {
    this.$edEventBus.$off(this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_SECTION_OBJECT_HOMEWORK));
  }

}